/* eslint-disable max-lines -- This route intentionally composes the complete transfer order workspace. */
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import {
  cn,
  DimeAlert,
  DimeAlertDescription,
  DimeAlertTitle,
  DimeButton,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
  type RowSelectionState,
  type SortingState,
} from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeftRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import { useBusinessDayNow } from '../../hooks/use-business-day-now.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { extractGraphqlErrorMessage } from '../../lib/graphql-error-message.js';
import { toastEntityUpdated } from '../../lib/toast-entity-updated.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { NewTransferOrderAction } from '../../modules/transfer-orders/new-transfer-order-action.js';
import { TransferOrderAttentionCard } from '../../modules/transfer-orders/transfer-order-attention-card.js';
import {
  GET_TRANSFER_ORDER_BRAND_OPTIONS,
  type GetTransferOrderBrandOptionsQueryResult,
} from '../../modules/transfer-orders/transfer-order-brand-options-operations.js';
import {
  describeBulkIssueProblems,
  fetchTransferOrderShortLines,
  issueTransferOrdersSequentially,
  partitionBulkIssueSelection,
  TransferOrderBulkIssueShortDialog,
} from '../../modules/transfer-orders/transfer-order-bulk-issue/index.js';
import { makeTransferOrderColumns } from '../../modules/transfer-orders/transfer-order-columns.js';
import { TransferOrderFilterMenu } from '../../modules/transfer-orders/transfer-order-filter-menu.js';
import { TransferOrderFilterSummary } from '../../modules/transfer-orders/transfer-order-filter-summary.js';
import {
  GET_TRANSFER_ORDER_SHORT_LINES,
  type GetTransferOrderShortLinesQueryResult,
  type GetTransferOrderShortLinesQueryVariables,
} from '../../modules/transfer-orders/transfer-order-lifecycle-operations.js';
import {
  countTransferOrderFilters,
  type TransferOrderListFilters,
} from '../../modules/transfer-orders/transfer-order-list-filters/index.js';
import {
  GET_TRANSFER_ORDER_METRICS,
  GET_TRANSFER_ORDERS,
  ISSUE_TRANSFER_ORDER,
  type GetTransferOrderMetricsQueryResult,
  type GetTransferOrderMetricsQueryVariables,
  type GetTransferOrdersQueryResult,
  type GetTransferOrdersQueryVariables,
  type IssueTransferOrderMutationResult,
  type IssueTransferOrderMutationVariables,
  type TransferOrderListItem,
  type TransferOrderWhere,
} from '../../modules/transfer-orders/transfer-order-list-operations.js';
import {
  buildTransferOrderListVariables,
  buildTransferOrderMetricsVariables,
  compatibleStatusIds,
  filtersFromWorkspaceSearch,
  isStatusCompatibleWithQueue,
  parseTransferOrderWorkspaceSearch,
  transferOrderQueueDefinition,
  workspaceSearchFromFilters,
  type TransferOrderWorkspaceSearch,
} from '../../modules/transfer-orders/transfer-order-list-utils/index.js';
import { TransferOrderStatusSummary } from '../../modules/transfer-orders/transfer-order-status-summary.js';
import type { TransferOrderWorkspaceQueue } from '../../modules/transfer-orders/transfer-order-workspace-queues.js';
import { useTransferOrderWarehouseOptions } from '../../modules/transfer-orders/use-transfer-order-warehouse-options.js';

const SEARCH_PLACEHOLDER = 'Search by number, brand, or item…';

export const TRANSFER_ORDER_SORT_MAP: Record<string, string[]> = {
  num: ['num'],
  status: ['statusId'],
  dateCreated: ['dateCreated'],
  dateScheduled: ['dateScheduled'],
};
const TRANSFER_ORDER_DEFAULT_SORT: SortingState = [{ id: 'dateScheduled', desc: false }];
const TRANSFER_ORDER_SORT_TIEBREAKER_FIELD = 'id';

export const Route = createFileRoute('/transfer-orders/')({
  validateSearch: (search: Record<string, unknown>): TransferOrderWorkspaceSearch =>
    parseTransferOrderWorkspaceSearch(search),
  component: TransferOrdersPage,
});

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function TransferOrdersPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canDelete = permissions.includes('transferOrder:delete');
  const canBulkIssue =
    permissions.includes('transferOrder:issue') && permissions.includes('part:view');
  const canViewBrands = permissions.includes('brand:view');
  const canViewLocationGroups = permissions.includes('locationGroup:view');
  const workspace = Route.useSearch();
  const queue = workspace.queue ?? 'open';
  const filters = useMemo(() => filtersFromWorkspaceSearch(workspace), [workspace]);
  const queueDefinition = transferOrderQueueDefinition(queue);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const clearSelection = useCallback(() => setRowSelection({}), []);
  const [bulkIssuing, setBulkIssuing] = useState(false);
  const [bulkIssueError, setBulkIssueError] = useState<string | null>(null);
  const [shortTransfers, setShortTransfers] = useState<
    GetTransferOrderShortLinesQueryResult['transferOrderShortLines'] | null
  >(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [listNow, reanchorListNow] = useBusinessDayNow();

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.altKey || event.ctrlKey || event.metaKey) return;
      if (isTypingTarget(event.target)) return;
      const searchInput = document.querySelector<HTMLInputElement>(
        `input[aria-label="${SEARCH_PLACEHOLDER}"]`,
      );
      if (searchInput) {
        event.preventDefault();
        searchInput.focus();
      }
    };
    document.addEventListener('keydown', focusSearch);
    return () => document.removeEventListener('keydown', focusSearch);
  }, []);

  const { data: brandsData } = useQuery<GetTransferOrderBrandOptionsQueryResult>(
    GET_TRANSFER_ORDER_BRAND_OPTIONS,
    { errorPolicy: 'all', skip: !canViewBrands },
  );
  const { warehouses, failed: warehousesFailed } =
    useTransferOrderWarehouseOptions(canViewLocationGroups);
  const brands = useMemo(() => brandsData?.transferOrderBrandOptions ?? [], [brandsData]);

  const listVariables = useMemo(
    () => buildTransferOrderListVariables({ queue, filters, now: listNow }),
    [queue, filters, listNow],
  );

  const {
    rows: transferOrders,
    loading,
    error,
    refetch,
    search,
    tableProps,
  } = useListTable<
    GetTransferOrdersQueryResult,
    GetTransferOrdersQueryVariables,
    TransferOrderListItem,
    TransferOrderWhere
  >({
    query: GET_TRANSFER_ORDERS,
    extractConnection: (data) => data?.transferOrders,
    defaultSort: TRANSFER_ORDER_DEFAULT_SORT,
    sortMap: TRANSFER_ORDER_SORT_MAP,
    sortTiebreakerField: TRANSFER_ORDER_SORT_TIEBREAKER_FIELD,
    where: listVariables.where,
    variables: {
      warehouseId: listVariables.warehouseId,
      inTransitMinDays: listVariables.inTransitMinDays,
      canViewBrands,
      canViewLocationGroups,
    },
  });

  const {
    data: metricsData,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<GetTransferOrderMetricsQueryResult, GetTransferOrderMetricsQueryVariables>(
    GET_TRANSFER_ORDER_METRICS,
    {
      variables: buildTransferOrderMetricsVariables({ filters, search }),
    },
  );
  const metrics = metricsError ? null : (metricsData?.transferOrderMetrics ?? null);

  const refreshAll = useCallback(async () => {
    setRefreshError(null);
    try {
      await Promise.all([refetch(), refetchMetrics()]);
    } catch {
      setRefreshError(
        'Your change was saved, but the list could not be refreshed. Reload to see the latest state.',
      );
    }
  }, [refetch, refetchMetrics]);

  const columns = useMemo(
    () =>
      makeTransferOrderColumns({
        canDelete,
        onDeleted: () => void refreshAll(),
        onDeleteRejected: () => void refreshAll(),
        rowOffset: tableProps.pageIndex * tableProps.pageSize,
        now: listNow,
      }),
    [canDelete, refreshAll, tableProps.pageIndex, tableProps.pageSize, listNow],
  );
  const { draftIds, skippedCount } = useMemo(
    () => partitionBulkIssueSelection(transferOrders, rowSelection),
    [transferOrders, rowSelection],
  );

  const [issueTransferOrder] = useMutation<
    IssueTransferOrderMutationResult,
    IssueTransferOrderMutationVariables
  >(ISSUE_TRANSFER_ORDER, { refetchQueries: [] });
  const apolloClient = useApolloClient();

  const navigateWorkspace = (
    patch: (previous: TransferOrderWorkspaceSearch) => TransferOrderWorkspaceSearch,
  ) => {
    clearSelection();
    setBulkIssueError(null);
    setRefreshError(null);
    reanchorListNow();
    void navigate({
      from: '/transfer-orders/',
      to: '/transfer-orders',
      search: (previous) => patch(previous),
    });
  };

  const applyFilters = (next: TransferOrderListFilters) =>
    navigateWorkspace((previous) => ({
      ...previous,
      ...workspaceSearchFromFilters({
        ...next,
        statusIds: compatibleStatusIds(queue, next.statusIds),
      }),
    }));

  const handleQueueChange = (nextQueue: TransferOrderWorkspaceQueue) =>
    navigateWorkspace((previous) => {
      const statusIds = compatibleStatusIds(nextQueue, previous.statusIds ?? []);
      return {
        ...previous,
        queue: nextQueue,
        statusIds: statusIds.length > 0 ? statusIds : undefined,
      };
    });

  const handleToggleStatus = (statusId: number) => {
    const statusIds = filters.statusIds.includes(statusId)
      ? filters.statusIds.filter((selected) => selected !== statusId)
      : [...filters.statusIds, statusId];
    const nextQueue = isStatusCompatibleWithQueue(queue, statusId) ? queue : 'all';
    navigateWorkspace((previous) => ({
      ...previous,
      queue: nextQueue,
      statusIds: statusIds.length > 0 ? statusIds : undefined,
    }));
  };

  const runBulkIssue = async () => {
    setBulkIssueError(null);
    setRefreshError(null);
    setBulkIssuing(true);
    try {
      const { succeededNums, failures } = await issueTransferOrdersSequentially(
        draftIds,
        async (id) => {
          const { data: mutationData } = await issueTransferOrder({ variables: { id } });
          const issued = mutationData?.issueTransferOrder;
          if (!issued) throw new Error(`Mutation returned no transfer order for ${id}`);
          return { num: issued.num };
        },
      );
      for (const num of succeededNums) toastEntityUpdated(num, 'issued');
      setBulkIssueError(
        describeBulkIssueProblems({
          failures: failures.map((failure) => ({
            num:
              transferOrders.find((transferOrder) => transferOrder.id === failure.id)?.num ??
              failure.id,
            error: failure.error,
          })),
          skippedCount,
        }),
      );
      await refreshAll();
    } finally {
      setBulkIssuing(false);
      setShortTransfers(null);
      clearSelection();
    }
  };

  const handleBulkIssue = async () => {
    if (draftIds.length === 0) return;
    setBulkIssueError(null);
    setRefreshError(null);
    setBulkIssuing(true);
    let short: GetTransferOrderShortLinesQueryResult['transferOrderShortLines'];
    try {
      short = await fetchTransferOrderShortLines(draftIds, async (ids) => {
        const { data: shortData } = await apolloClient.query<
          GetTransferOrderShortLinesQueryResult,
          GetTransferOrderShortLinesQueryVariables
        >({
          query: GET_TRANSFER_ORDER_SHORT_LINES,
          variables: { ids, canViewUoms: permissions.includes('uom:view') },
          fetchPolicy: 'network-only',
        });
        return shortData?.transferOrderShortLines ?? [];
      });
    } catch (err) {
      setBulkIssueError(
        extractGraphqlErrorMessage(err, 'Could not check stock for the selected transfers.'),
      );
      setBulkIssuing(false);
      return;
    }
    if (short.length > 0) {
      setShortTransfers(short);
      setBulkIssuing(false);
      return;
    }
    await runBulkIssue();
  };

  const isNarrowed = Boolean(search) || countTransferOrderFilters(filters) > 0;
  const isFirstRun =
    queue === 'all' && !loading && !error && transferOrders.length === 0 && !isNarrowed;

  const listTableProps = {
    ...tableProps,
    onNextPage: () => {
      clearSelection();
      tableProps.onNextPage();
    },
    onPreviousPage: () => {
      clearSelection();
      tableProps.onPreviousPage();
    },
    onSearchChange: (value: string) => {
      clearSelection();
      tableProps.onSearchChange(value);
    },
  };

  const renderList = () => {
    if (error && transferOrders.length === 0) {
      return (
        <ListErrorState
          title="Could not load transfer orders"
          message={error.message}
          onRetry={() => void refetch()}
        />
      );
    }
    if (isFirstRun) {
      return (
        <ListEmptyState
          icon={<ArrowLeftRight />}
          title={queueDefinition.emptyTitle}
          description={queueDefinition.emptyDescription}
          action={<NewTransferOrderAction />}
        />
      );
    }
    return (
      <div className="overflow-x-auto">
        <DimeDataTable
          {...listTableProps}
          columns={columns}
          data={transferOrders}
          getRowId={(transferOrder) => transferOrder.id}
          isLoading={loading}
          loadingRowCount={tableProps.pageSize}
          exportFileName="transfer-orders"
          searchColumn="num"
          searchPlaceholder={SEARCH_PLACEHOLDER}
          actions={
            <>
              <TransferOrderFilterSummary
                filters={filters}
                brands={brands}
                warehouses={warehouses}
                onChange={applyFilters}
              />
              <TransferOrderFilterMenu
                filters={filters}
                statusIds={queueDefinition.statusIds}
                brands={brands}
                warehouses={warehouses}
                onChange={applyFilters}
              />
            </>
          }
          onRowClick={(transferOrder) =>
            void navigate({
              to: '/transfer-orders/$transferOrderId',
              params: { transferOrderId: transferOrder.id },
            })
          }
          showViewOptions
          showSelect={canBulkIssue}
          selected={rowSelection}
          onUpdateSelected={setRowSelection}
          emptyMessage={
            isNarrowed
              ? 'No transfer orders found. Try adjusting your search or filters.'
              : `${queueDefinition.emptyTitle}. ${queueDefinition.emptyDescription}`
          }
        />
      </div>
    );
  };

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Transfer Orders</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Move inventory between warehouses and track what is still in transit.
        </DimeCardDescription>
        <DimeCardAction>
          <NewTransferOrderAction />
        </DimeCardAction>
      </DimeCardHeader>
      <div className="min-w-0 space-y-3">
        {metricsError && (
          <ListErrorState
            title="Could not load transfer order metrics"
            message={metricsError.message}
            onRetry={() => void refetchMetrics()}
          />
        )}
        <div
          className={cn(
            'grid gap-3',
            !metricsError && 'lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]',
          )}
        >
          {!metricsError && (
            <TransferOrderStatusSummary
              metrics={metrics}
              activeStatusIds={filters.statusIds}
              onToggleStatus={handleToggleStatus}
            />
          )}
          <TransferOrderAttentionCard
            brandId={filters.brandId || undefined}
            warehouseId={filters.warehouseId || undefined}
            metrics={metrics}
            queue={queue}
            onQueueChange={handleQueueChange}
          />
        </div>
        {warehousesFailed && (
          <DimeAlert variant="destructive" className="mb-0" role="alert">
            <DimeAlertTitle>Warehouse filter unavailable</DimeAlertTitle>
            <DimeAlertDescription>
              Warehouses could not be loaded, so the warehouse filter is empty. Reload to try again.
            </DimeAlertDescription>
          </DimeAlert>
        )}
        {refreshError && (
          <DimeAlert variant="destructive" className="mb-0">
            <DimeAlertTitle>List not refreshed</DimeAlertTitle>
            <DimeAlertDescription>{refreshError}</DimeAlertDescription>
          </DimeAlert>
        )}
        {bulkIssueError && (
          <DimeAlert variant="destructive" className="mb-0">
            <DimeAlertTitle>Some transfer orders were not issued</DimeAlertTitle>
            <DimeAlertDescription>{bulkIssueError}</DimeAlertDescription>
          </DimeAlert>
        )}
        {canBulkIssue && draftIds.length > 0 && (
          <div className="flex justify-end">
            <DimeButton type="button" disabled={bulkIssuing} onClick={() => void handleBulkIssue()}>
              {bulkIssuing ? 'Issuing…' : 'Issue selected'}
            </DimeButton>
          </div>
        )}
        {renderList()}
      </div>
      {shortTransfers && (
        <TransferOrderBulkIssueShortDialog
          shortTransfers={shortTransfers}
          issuing={bulkIssuing}
          onCancel={() => setShortTransfers(null)}
          onConfirm={() => void runBulkIssue()}
        />
      )}
    </DimeCard>
  );
}
